import React, { useMemo, useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';
import { Tree, type TreeApi } from 'react-arborist';
import { Text, Loader, Alert, Select, ActionIcon, Tooltip, Box, TextInput } from '@mantine/core';
import {
  IconPlus,
  IconCopy,
  IconStack2,
  IconPencil,
  IconX,
  IconChevronDown,
} from '@tabler/icons-react';
import { useLocationsTreeStore } from '../stores/locationsTreeStore';
import type { LocationTreeNode } from '../api/schedulingApis';
import { locationTreeIdAccessor, locationTreeChildrenAccessor, locationTreeManagementChildrenAccessor } from './adapters';
import {
  formatLocationTypeLabel,
  isProjectRootNode,
  collectLocationTypes,
  normalizeLocationNameInput,
} from './locationTreeHelpers';
import {
  annotateLocationTypeOptions,
  canDeleteLocationNode,
  canPlaceLocationType,
  findParentNode,
  isFloorLocationType,
  normalizeLocationTypeKey,
  resolveLocationTypeInput,
} from '../features/schedule-locations/logic/locationTypeRules';

/** Flatten tree to count all nodes */
function countNodes(nodes: LocationTreeNode[]): number {
  return nodes.reduce((acc, n) => 1 + acc + countNodes(n.child ?? []), 0);
}

const ADD_PLACEHOLDER_TYPE = '__add_placeholder__';
const ADD_COMPOSER_TYPE = '__add_composer__';

function isAddPlaceholder(node: LocationTreeNode): boolean {
  return String(node.location_type) === ADD_PLACEHOLDER_TYPE;
}

function isAddComposer(node: LocationTreeNode): boolean {
  return String(node.location_type) === ADD_COMPOSER_TYPE;
}

function isSyntheticRow(node: LocationTreeNode): boolean {
  return isAddPlaceholder(node) || isAddComposer(node);
}

/**
 * Volatile tree chrome (inline edit/add) lives in context so arborist's
 * `children` / `renderRow` component types stay stable across keystrokes.
 * Inline functions as Row/Node remount the input on every render and steal focus.
 */
type LocationTreeChromeContextValue = {
  onRemoveNode?: (node: LocationTreeNode) => void;
  onQuickSelectLocation?: (location: LocationTreeNode) => void;
  /** Picker: row click focuses this location in the parent (independent of arborist selection). */
  onFocusLocation?: (location: LocationTreeNode) => void;
  quickSelectLabel: string;
  enabledLocationIds?: Set<string> | null;
  showRenameHint: boolean;
  variant: 'default' | 'management' | 'picker';
  onStartAddChild?: (parent: LocationTreeNode) => void;
  onDuplicateNode?: (node: LocationTreeNode) => void;
  onChangeType?: (node: LocationTreeNode, locationType: string) => void;
  locationTypeOptions: { value: string; label: string }[];
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  editingId: string | null;
  editName: string;
  onStartEdit: (node: LocationTreeNode) => void;
  onEditNameChange: (value: string) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  addName: string;
  addType: string;
  onAddNameChange: (value: string) => void;
  onAddTypeChange: (value: string) => void;
  onConfirmAdd: () => void;
  onCancelAdd: () => void;
  typeOptionsForAdd: { value: string; label: string; disabled?: boolean }[];
  /** Tree used for type-placement / delete rules (no synthetic rows). */
  rulesTreeData: LocationTreeNode[];
  highlightedLocationIds?: Set<string>;
  activeLocationId: string | null;
  /** Locations touched by a pending location-chat proposal, keyed by how to mark them. */
  pendingChatMarks?: ReadonlyMap<string, 'new' | 'update' | 'remove'>;
};

const LocationTreeChromeContext = createContext<LocationTreeChromeContextValue | null>(null);

function useLocationTreeChrome(): LocationTreeChromeContextValue {
  const ctx = useContext(LocationTreeChromeContext);
  if (!ctx) {
    throw new Error('Location tree chrome context missing');
  }
  return ctx;
}

function realChildCount(node: LocationTreeNode): number {
  return (node.child ?? []).filter((c) => !isSyntheticRow(c)).length;
}

function findNodeById(nodes: LocationTreeNode[], id: string): LocationTreeNode | null {
  for (const n of nodes) {
    if (String(n.location_id) === String(id)) return n;
    const hit = findNodeById(n.child ?? [], id);
    if (hit) return hit;
  }
  return null;
}

/**
 * Inject display-only rows for management mode:
 * - inline add composer under `addParentId` (even if that parent is a leaf)
 * - “+ Add under …” link under non-leaf parents, or under the parent being composed into
 */
export function injectManagementRows(
  nodes: LocationTreeNode[],
  addParentId: string | null,
): LocationTreeNode[] {
  return nodes.map((n) => {
    const id = String(n.location_id ?? '');
    let kids = injectManagementRows(n.child ?? [], addParentId).filter((c) => !isSyntheticRow(c));
    const isAddTarget = addParentId != null && addParentId === id;

    if (isAddTarget) {
      kids = [
        {
          location_id: `__composer__${id}`,
          name: '',
          location_type: ADD_COMPOSER_TYPE,
          location_level: (n.location_level ?? 1) + 1,
          location_group: [],
          parent_location_id: n.location_id ?? null,
          meta: { parentName: n.name, parentId: id },
          child: [],
        },
        ...kids,
      ];
    }

    const realCount = kids.filter((c) => !isSyntheticRow(c)).length;
    const isRoot = isProjectRootNode(n);

    if (isRoot) {
      kids = [
        ...kids,
        {
          location_id: `__add_top__${id}`,
          name: '',
          location_type: ADD_PLACEHOLDER_TYPE,
          location_level: (n.location_level ?? 1) + 1,
          location_group: [],
          parent_location_id: n.location_id ?? null,
          meta: { parentName: n.name, isTopLevelAdd: true },
          child: [],
        },
      ];
    } else if (realCount > 0 || isAddTarget) {
      kids = [
        ...kids,
        {
          location_id: `__add__${id}`,
          name: '',
          location_type: ADD_PLACEHOLDER_TYPE,
          location_level: (n.location_level ?? 1) + 1,
          location_group: [],
          parent_location_id: n.location_id ?? null,
          meta: { parentName: n.name },
          child: [],
        },
      ];
    }

    return { ...n, child: kids };
  });
}

function LocationTreeRow<T>({
  node,
  innerRef,
  attrs,
  children,
  enabledLocationIds,
  highlightedLocationIds,
  activeLocationId,
  editingId,
}: {
  node: { isSelected: boolean; data: LocationTreeNode };
  innerRef: (el: HTMLDivElement | null) => void;
  attrs: React.HTMLAttributes<HTMLDivElement>;
  children: React.ReactElement;
  enabledLocationIds?: Set<string> | null;
  highlightedLocationIds?: Set<string>;
  activeLocationId?: string | null;
  editingId?: string | null;
}) {
  const locationId = node.data?.location_id != null ? String(node.data.location_id) : '';
  const disabled =
    enabledLocationIds != null && locationId !== '' && !enabledLocationIds.has(locationId);
  const synthetic = isSyntheticRow(node.data);
  const isEditing = !synthetic && editingId != null && editingId === locationId;
  const isHighlighted =
    highlightedLocationIds != null
      ? highlightedLocationIds.has(locationId)
      : node.isSelected;
  const isActive = activeLocationId != null && activeLocationId === locationId;
  const chrome = useContext(LocationTreeChromeContext);
  const useMgmtChrome = chrome?.variant === 'management' || chrome?.variant === 'picker';
  const picker = chrome?.variant === 'picker';
  const pendingMark = chrome?.pendingChatMarks?.get(locationId);
  return (
    <div
      {...attrs}
      ref={innerRef}
      className={[
        'location-tree-row',
        useMgmtChrome ? 'location-tree-row--mgmt' : '',
        picker ? 'location-tree-row--picker' : '',
        synthetic ? 'is-add-placeholder' : '',
        isAddComposer(node.data) ? 'is-add-composer' : '',
        isEditing ? 'is-inline-editing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-selected={synthetic || isEditing ? false : isHighlighted}
      data-active={synthetic || isEditing ? false : isActive}
      data-disabled={disabled}
      data-chat-pending={pendingMark ?? undefined}
      style={{
        ...attrs.style,
        ...(disabled
          ? {
              opacity: 0.55,
              // Picker: still allow focusing out-of-scope rows; Set stays disabled.
              cursor: (picker ? 'pointer' : 'not-allowed') as React.CSSProperties['cursor'],
            }
          : {}),
      }}
      onFocus={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (synthetic || isEditing || pendingMark === 'new') {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // Management/default: block clicks on out-of-scope rows.
        // Picker: allow focus; scope is enforced only by the Set button.
        if (disabled && !picker) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        (node as { handleClick?: (ev: React.MouseEvent) => void }).handleClick?.(e);
        if (picker && !synthetic) {
          chrome?.onFocusLocation?.(node.data);
        }
      }}
    >
      {children}
    </div>
  );
}

export interface LocationHierarchyTreeProps {
  projectId: string;
  onSelectLocation?: (location: LocationTreeNode) => void;
  onSelectionChange?: (nodes: LocationTreeNode[]) => void;
  onQuickSelectLocation?: (location: LocationTreeNode) => void;
  quickSelectLabel?: string;
  onDataLoad?: (data: LocationTreeNode[], nodeCount: number) => void;
  onRename?: (params: { id: string; name: string }) => void | Promise<void>;
  showRenameHint?: boolean;
  selectedLocationId?: string | null;
  highlightedLocationIds?: Set<string>;
  activeLocationId?: string | null;
  /** Locations touched by a pending location-chat proposal, keyed by how to mark them. */
  pendingChatMarks?: ReadonlyMap<string, 'new' | 'update' | 'remove'>;
  width?: number | string;
  height?: number;
  disableDrag?: boolean;
  disableEdit?: boolean;
  searchTerm?: string;
  searchMatch?: (node: { data: LocationTreeNode }, term: string) => boolean;
  enabledLocationIds?: Set<string> | null;
  dataOverride?: LocationTreeNode[] | null;
  onMove?: (params: { dragIds: string[]; parentId: string | null; index: number }) => void;
  onRemoveNode?: (node: LocationTreeNode) => void;
  treeApiRef?: React.Ref<TreeApi<LocationTreeNode> | undefined | null>;
  variant?: 'default' | 'management' | 'picker';
  /** @deprecated Prefer onConfirmAddChild — management mode starts an inline composer instead. */
  onAddChild?: (parent: LocationTreeNode) => void;
  /** Called when user confirms the inline add row (name + type). Does not select the new node. */
  onConfirmAddChild?: (params: {
    parent: LocationTreeNode;
    name: string;
    locationType: string;
  }) => void;
  /** When set, management tree opens the inline add composer under this parent id. */
  pendingAddParentId?: string | null;
  onPendingAddParentConsumed?: () => void;
  /** Controlled inline add composer state (management mode). */
  inlineAdd?: { parentId: string; name: string; locationType: string } | null;
  onInlineAddChange?: (value: { parentId: string; name: string; locationType: string } | null) => void;
  onDuplicateNode?: (node: LocationTreeNode) => void;
  onChangeType?: (node: LocationTreeNode, locationType: string) => void;
  locationTypeOptions?: { value: string; label: string }[];
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export { countNodes };

function LocationNode({
  node,
  style,
  dragHandle,
  onRemoveNode,
  onQuickSelectLocation,
  quickSelectLabel = 'Set',
  enabledLocationIds,
  showRenameHint = true,
  variant = 'default',
  onStartAddChild,
  onDuplicateNode,
  onChangeType,
  locationTypeOptions = [],
  canCreate = true,
  canEdit = true,
  canDelete = true,
  editingId,
  editName,
  onStartEdit,
  onEditNameChange,
  onCommitEdit,
  onCancelEdit,
  addName,
  addType,
  onAddNameChange,
  onAddTypeChange,
  onConfirmAdd,
  onCancelAdd,
  typeOptionsForAdd,
  rulesTreeData = [],
}: {
  node: {
    data: LocationTreeNode;
    isLeaf: boolean;
    isOpen: boolean;
    isSelected: boolean;
    toggle: () => void;
    edit?: () => void;
  };
  style: React.CSSProperties;
  dragHandle?: (el: HTMLDivElement | null) => void;
  onRemoveNode?: (node: LocationTreeNode) => void;
  onQuickSelectLocation?: (location: LocationTreeNode) => void;
  quickSelectLabel?: string;
  enabledLocationIds?: Set<string> | null;
  showRenameHint?: boolean;
  variant?: 'default' | 'management' | 'picker';
  onStartAddChild?: (parent: LocationTreeNode) => void;
  onDuplicateNode?: (node: LocationTreeNode) => void;
  onChangeType?: (node: LocationTreeNode, locationType: string) => void;
  locationTypeOptions?: { value: string; label: string }[];
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  editingId?: string | null;
  editName?: string;
  onStartEdit?: (node: LocationTreeNode) => void;
  onEditNameChange?: (value: string) => void;
  onCommitEdit?: () => void;
  onCancelEdit?: () => void;
  addName?: string;
  addType?: string;
  onAddNameChange?: (value: string) => void;
  onAddTypeChange?: (value: string) => void;
  onConfirmAdd?: () => void;
  onCancelAdd?: () => void;
  typeOptionsForAdd?: { value: string; label: string; disabled?: boolean }[];
  rulesTreeData?: LocationTreeNode[];
}) {
  const d = node.data;
  const indentLeft = Number(style.paddingLeft) || 0;
  const management = variant === 'management';
  const nameSelectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (nameSelectTimerRef.current) clearTimeout(nameSelectTimerRef.current);
    },
    [],
  );

  if (isAddComposer(d)) {
    const composerTypeData = typeOptionsForAdd ?? [{ value: 'general', label: 'General' }];
    return (
      <div
        ref={dragHandle}
        className="location-tree-node-mgmt location-tree-node-mgmt--composer"
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          paddingTop: 8,
          paddingBottom: 8,
          paddingRight: 16,
          paddingLeft: indentLeft + 36,
          boxSizing: 'border-box',
          height: 45,
          minHeight: 45,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="location-tree-node-mgmt-main">
          <span className="location-tree-node-mgmt-chevron" aria-hidden />
          <TextInput
            size="xs"
            placeholder="Location name"
            value={addName ?? ''}
            onChange={(e) => onAddNameChange?.(normalizeLocationNameInput(e.currentTarget.value))}
            autoFocus
            className="location-tree-node-mgmt-composer-name"
            style={{ flex: 1, minWidth: 0 }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                onConfirmAdd?.();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancelAdd?.();
              }
            }}
          />
        </div>
        <div className="location-tree-node-mgmt-meta">
          <span className="location-tree-node-mgmt-count" aria-hidden style={{ visibility: 'hidden' }}>
            0
          </span>
          <Select
            className="location-tree-node-mgmt-type"
            size="xs"
            data={composerTypeData}
            value={
              resolveLocationTypeInput(
                addType ?? 'general',
                composerTypeData.map((o) => o.value),
              ) || 'general'
            }
            onChange={(v) => {
              if (!v) return;
              onAddTypeChange?.(v);
            }}
            allowDeselect={false}
            withCheckIcon={false}
            rightSection={<IconChevronDown size={12} stroke={1.75} color="#4D5666" />}
            rightSectionPointerEvents="none"
            renderOption={({ option, checked }) => (
              <span
                className={[
                  'location-tree-node-mgmt-type-option',
                  checked ? 'is-selected' : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {option.label}
              </span>
            )}
            comboboxProps={{ withinPortal: true, zIndex: 1200 }}
            styles={{ dropdown: { minWidth: 180 } }}
          />
          <Box className="location-tree-node-mgmt-actions location-tree-node-mgmt-actions--visible">
            <Tooltip label="Add location" withArrow>
              <ActionIcon
                className="location-tree-node-mgmt-action location-tree-node-mgmt-action--add"
                size={27}
                variant="subtle"
                disabled={!(addName ?? '').trim()}
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirmAdd?.();
                }}
              >
                <IconPlus size={14} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Duplicate" withArrow>
              <ActionIcon
                className="location-tree-node-mgmt-action location-tree-node-mgmt-action--copy"
                size={27}
                variant="subtle"
                disabled
              >
                <IconCopy size={14} stroke={1.4} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Cancel" withArrow>
              <ActionIcon
                className="location-tree-node-mgmt-action location-tree-node-mgmt-action--delete"
                size={27}
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelAdd?.();
                }}
              >
                <IconX size={14} stroke={1.4} />
              </ActionIcon>
            </Tooltip>
          </Box>
        </div>
      </div>
    );
  }

  if (isAddPlaceholder(d)) {
    const parentName = String((d.meta as { parentName?: string } | undefined)?.parentName ?? 'this location');
    const isTopLevelAdd = Boolean((d.meta as { isTopLevelAdd?: boolean } | undefined)?.isTopLevelAdd);
    const parentId = d.parent_location_id != null ? String(d.parent_location_id) : '';
    return (
      <div
        ref={dragHandle}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: isTopLevelAdd ? indentLeft + 8 : indentLeft + 24,
          paddingRight: isTopLevelAdd ? 16 : 8,
          minHeight: isTopLevelAdd ? 45 : 32,
        }}
      >
        <button
          type="button"
          className={isTopLevelAdd ? 'location-tree-add-top-level' : 'location-tree-add-under'}
          disabled={!canCreate}
          onClick={(e) => {
            e.stopPropagation();
            if (!canCreate || !onStartAddChild || !parentId) return;
            onStartAddChild({
              location_id: parentId,
              name: parentName,
              location_type: 'general',
              location_level: (d.location_level ?? 2) - 1,
              location_group: [],
              parent_location_id: null,
              meta: {},
              child: [],
            });
          }}
        >
          {isTopLevelAdd ? '+ Add a top-level location' : `+ Add a location under ${parentName}`}
        </button>
      </div>
    );
  }

  const hasChildren = realChildCount(d) > 0;
  const typeLabel = d.location_type
    ? formatLocationTypeLabel(String(d.location_type))
    : '';
  const locationId = d.location_id != null ? String(d.location_id) : '';
  const disabled =
    enabledLocationIds != null && locationId !== '' && !enabledLocationIds.has(locationId);
  const picker = variant === 'picker';
  const useMgmtChrome = management || picker;
  const chromeRowHeight = picker ? 36 : management ? 45 : undefined;
  const chromePadY = picker ? 5 : management ? 8 : undefined;
  const chromePadRight = useMgmtChrome ? (picker ? 12 : 16) : 8;
  const chromePadLeftExtra = picker ? 32 : 36;
  const canInlineEdit =
    management
      ? canEdit && Boolean(onStartEdit)
      : !picker && typeof (node as { edit?: () => void }).edit === 'function';
  const isRoot = isProjectRootNode(d);
  const childCount = realChildCount(d);
  const isEditing = management && editingId != null && editingId === locationId;
  const nodeDeletable = canDeleteLocationNode(d, rulesTreeData);
  const deleteDisabled = !canDelete || !nodeDeletable || !onRemoveNode;
  const deleteTooltip = !nodeDeletable
    ? (String(d.location_type || '').toLowerCase() === 'project'
        ? 'The only Project location cannot be deleted'
        : 'Root cannot be deleted')
    : 'Delete';

  const baseTypeOptions = locationTypeOptions.length
    ? locationTypeOptions
    : collectLocationTypes(rulesTreeData.length ? rulesTreeData : [d]).map((t) => ({
        value: t,
        label: formatLocationTypeLabel(t),
      }));
  const parentForRules = findParentNode(rulesTreeData, d);
  const currentTypeRaw = String(d.location_type || (isRoot ? 'project' : 'general'));
  const typeSelectData = isRoot
    ? [{ value: 'project', label: 'Project' }]
    : annotateLocationTypeOptions(
        baseTypeOptions
          .filter((o) => normalizeLocationTypeKey(o.value) !== 'project')
          .map((o) => ({
            value: o.value,
            label: o.label || formatLocationTypeLabel(o.value),
          })),
        parentForRules,
        rulesTreeData,
        { excludeNodeId: locationId || null },
      ).map((o) =>
        normalizeLocationTypeKey(o.value) === normalizeLocationTypeKey(currentTypeRaw)
          ? { ...o, disabled: false }
          : o,
      );
  const currentTypeValue =
    resolveLocationTypeInput(
      currentTypeRaw,
      typeSelectData.map((o) => o.value),
    ) || currentTypeRaw;

  return (
    <div
      ref={dragHandle}
      className={[
        useMgmtChrome ? 'location-tree-node-mgmt' : undefined,
        picker ? 'location-tree-node-mgmt--picker' : undefined,
        isEditing ? 'location-tree-inline-edit-row' : undefined,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: useMgmtChrome ? 'space-between' : undefined,
        gap: useMgmtChrome ? 8 : 0,
        paddingTop: chromePadY,
        paddingBottom: chromePadY,
        paddingRight: chromePadRight,
        paddingLeft: useMgmtChrome ? indentLeft + chromePadLeftExtra : indentLeft + 8,
        boxSizing: 'border-box',
        height: chromeRowHeight,
        minHeight: chromeRowHeight ?? 32,
        marginTop: picker ? 2 : undefined,
        marginBottom: picker ? 2 : undefined,
      }}
    >
      {useMgmtChrome ? (
        <div className="location-tree-node-mgmt-main">
          <span
            className="location-tree-node-mgmt-chevron"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) node.toggle();
            }}
            style={{ cursor: hasChildren ? 'pointer' : 'default' }}
          >
            {hasChildren ? (node.isOpen ? '▼' : '▶') : ''}
          </span>

          {isEditing ? (
            <TextInput
              size="xs"
              value={editName ?? ''}
              onChange={(e) => onEditNameChange?.(normalizeLocationNameInput(e.currentTarget.value))}
              autoFocus
              className="location-tree-node-mgmt-edit-input"
              style={{ flex: 1, minWidth: 0 }}
              styles={{ input: { fontSize: 14, minHeight: 29, fontWeight: 600 } }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onCommitEdit?.();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEdit?.();
                }
              }}
              onBlur={() => {
                window.setTimeout(() => {
                  const active = document.activeElement as HTMLElement | null;
                  if (
                    active?.closest?.(
                      '.location-tree-row.is-inline-editing, .location-tree-row.is-add-composer, .location-tree-node-mgmt--composer',
                    )
                  ) {
                    return;
                  }
                  onCommitEdit?.();
                }, 0);
              }}
            />
          ) : (
            <span
              className="location-tree-node-mgmt-title"
              onMouseDown={(e) => {
                if (canInlineEdit && e.detail > 1) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onClick={(e) => {
                if (!canInlineEdit) return;
                e.stopPropagation();
                if (nameSelectTimerRef.current) clearTimeout(nameSelectTimerRef.current);
                nameSelectTimerRef.current = setTimeout(() => {
                  nameSelectTimerRef.current = null;
                  if (document.querySelector('.location-tree-row.is-inline-editing')) return;
                  const api = node as { select?: () => void; activate?: () => void };
                  api.select?.();
                  api.activate?.();
                }, 220);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (nameSelectTimerRef.current) {
                  clearTimeout(nameSelectTimerRef.current);
                  nameSelectTimerRef.current = null;
                }
                if (!canInlineEdit) return;
                onStartEdit?.(d);
              }}
            >
              <span
                className={[
                  'location-tree-node-mgmt-name',
                  !hasChildren ? 'is-leaf' : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {d.name}
              </span>
              {canInlineEdit ? (
                <Tooltip label="Edit location name" withArrow>
                  <IconPencil
                    className="location-tree-node-mgmt-edit"
                    size={14}
                    stroke={1.2}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onStartEdit?.(d);
                    }}
                  />
                </Tooltip>
              ) : null}
            </span>
          )}
        </div>
      ) : (
        <>
          <span
            style={{
              width: 16,
              flexShrink: 0,
              color: 'var(--mantine-color-dimmed)',
              fontSize: 10,
              cursor: hasChildren ? 'pointer' : 'default',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) node.toggle();
            }}
          >
            {hasChildren ? (node.isOpen ? '▼' : '▶') : '•'}
          </span>

          {isEditing ? (
            <TextInput
              size="xs"
              value={editName ?? ''}
              onChange={(e) => onEditNameChange?.(normalizeLocationNameInput(e.currentTarget.value))}
              autoFocus
              style={{ flex: 1, minWidth: 0 }}
              styles={{ input: { fontSize: 13, minHeight: 30, fontWeight: isRoot ? 600 : 500 } }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onCommitEdit?.();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEdit?.();
                }
              }}
              onBlur={() => {
                window.setTimeout(() => {
                  const active = document.activeElement as HTMLElement | null;
                  if (
                    active?.closest?.(
                      '.location-tree-row.is-inline-editing, .location-tree-row.is-add-composer, .location-tree-node-mgmt--composer',
                    )
                  ) {
                    return;
                  }
                  onCommitEdit?.();
                }, 0);
              }}
            />
          ) : (
            <span
              style={{
                flex: 1,
                fontWeight: isRoot ? 600 : 500,
                fontSize: 14,
                color: 'var(--mantine-color-text)',
                minWidth: 0,
                cursor: canInlineEdit ? 'text' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                overflow: 'hidden',
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!canInlineEdit) return;
                (node as { edit: () => void }).edit();
              }}
              title={canInlineEdit && showRenameHint ? 'Double-click to rename' : undefined}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.name}
              </span>
            </span>
          )}
        </>
      )}

      {management ? (
        <div className="location-tree-node-mgmt-meta">
          {childCount > 0 ? (
            <span className="location-tree-node-mgmt-count">{childCount}</span>
          ) : null}
          {isRoot ? (
            <Select
              className="location-tree-node-mgmt-type"
              size="xs"
              data={[{ value: 'project', label: 'Project' }]}
              value="project"
              disabled
              onClick={(e) => e.stopPropagation()}
              allowDeselect={false}
              withCheckIcon={false}
              rightSection={<IconChevronDown size={12} stroke={1.75} color="#4D5666" />}
              rightSectionPointerEvents="none"
              comboboxProps={{ withinPortal: true, zIndex: 1200 }}
              styles={{ dropdown: { minWidth: 180 } }}
            />
          ) : (
            <Select
              className="location-tree-node-mgmt-type"
              size="xs"
              data={typeSelectData}
              value={currentTypeValue}
              disabled={!canEdit || !onChangeType || isEditing}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onChange={(v) => {
                if (!v || !onChangeType) return;
                onChangeType(d, v);
              }}
              allowDeselect={false}
              withCheckIcon={false}
              rightSection={<IconChevronDown size={12} stroke={1.75} color="#4D5666" />}
              rightSectionPointerEvents="none"
              renderOption={({ option, checked }) => (
                <span
                  className={[
                    'location-tree-node-mgmt-type-option',
                    checked ? 'is-selected' : undefined,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {option.label}
                </span>
              )}
              comboboxProps={{ withinPortal: true, zIndex: 1200 }}
              styles={{ dropdown: { minWidth: 180 } }}
            />
          )}
          <Box className="location-tree-row-actions location-tree-node-mgmt-actions">
            <Tooltip label="Add child" withArrow>
              <ActionIcon
                className="location-tree-node-mgmt-action location-tree-node-mgmt-action--add"
                size={27}
                variant="subtle"
                disabled={!canCreate || !onStartAddChild}
                onClick={(e) => {
                  e.stopPropagation();
                  onStartAddChild?.(d);
                }}
              >
                <IconPlus size={14} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={isFloorLocationType(d.location_type) ? 'Add next floor' : 'Duplicate'}
              withArrow
            >
              <ActionIcon
                className="location-tree-node-mgmt-action location-tree-node-mgmt-action--copy"
                size={27}
                variant="subtle"
                disabled={!canCreate || isRoot || !onDuplicateNode}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isRoot) onDuplicateNode?.(d);
                }}
              >
                {isFloorLocationType(d.location_type) ? (
                  <IconStack2 size={14} stroke={1.5} />
                ) : (
                  <IconCopy size={14} stroke={1.4} />
                )}
              </ActionIcon>
            </Tooltip>
            <Tooltip label={deleteTooltip} withArrow>
              <ActionIcon
                className="location-tree-node-mgmt-action location-tree-node-mgmt-action--delete"
                size={27}
                variant="subtle"
                disabled={deleteDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!deleteDisabled) onRemoveNode?.(d);
                }}
              >
                <IconX size={14} stroke={1.4} />
              </ActionIcon>
            </Tooltip>
          </Box>
        </div>
      ) : picker ? (
        <div className="location-tree-node-mgmt-meta location-tree-node-mgmt-meta--picker">
          {childCount > 0 ? (
            <span className="location-tree-node-mgmt-count">{childCount}</span>
          ) : null}
          {typeLabel ? <span className="location-tree-type-badge">{typeLabel}</span> : null}
          {onQuickSelectLocation ? (
            <button
              type="button"
              className="location-tree-set-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (disabled) return;
                onQuickSelectLocation(d);
              }}
              disabled={disabled}
              title={disabled ? 'Not available' : 'Set this location'}
            >
              {quickSelectLabel}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <span
            style={{
              width: 72,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {typeLabel ? <span className="location-tree-type-badge">{typeLabel}</span> : null}
          </span>
          {onQuickSelectLocation && (
            <button
              type="button"
              className="location-tree-set-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (disabled) return;
                onQuickSelectLocation(d);
              }}
              disabled={disabled}
              title={disabled ? 'Not available' : 'Set this location'}
              style={{
                width: 44,
                height: 26,
                flexShrink: 0,
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid var(--mantine-color-default-border, #dee2e6)',
                background: 'transparent',
                color: 'var(--mantine-color-text)',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {quickSelectLabel}
            </button>
          )}
          {onRemoveNode && (
            <button
              type="button"
              title="Remove node"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveNode(d);
              }}
              style={{
                width: 24,
                height: 24,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: 'none',
                borderRadius: 4,
                background: 'transparent',
                color: 'var(--mantine-color-red-6, #fa5252)',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: 1,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              −
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Stable arborist node type — must not be recreated when editName changes. */
function ArboristLocationNode({
  node,
  style,
  dragHandle,
}: {
  node: {
    data: LocationTreeNode;
    isLeaf: boolean;
    isOpen: boolean;
    isSelected: boolean;
    toggle: () => void;
    edit?: () => void;
  };
  style: React.CSSProperties;
  dragHandle?: (el: HTMLDivElement | null) => void;
}) {
  const chrome = useLocationTreeChrome();
  return (
    <LocationNode
      node={node}
      style={style}
      dragHandle={dragHandle}
      onRemoveNode={chrome.onRemoveNode}
      onQuickSelectLocation={chrome.onQuickSelectLocation}
      quickSelectLabel={chrome.quickSelectLabel}
      enabledLocationIds={chrome.enabledLocationIds}
      showRenameHint={chrome.showRenameHint}
      variant={chrome.variant}
      onStartAddChild={chrome.onStartAddChild}
      onDuplicateNode={chrome.onDuplicateNode}
      onChangeType={chrome.onChangeType}
      locationTypeOptions={chrome.locationTypeOptions}
      canCreate={chrome.canCreate}
      canEdit={chrome.canEdit}
      canDelete={chrome.canDelete}
      editingId={chrome.editingId}
      editName={chrome.editName}
      onStartEdit={chrome.onStartEdit}
      onEditNameChange={chrome.onEditNameChange}
      onCommitEdit={chrome.onCommitEdit}
      onCancelEdit={chrome.onCancelEdit}
      addName={chrome.addName}
      addType={chrome.addType}
      onAddNameChange={chrome.onAddNameChange}
      onAddTypeChange={chrome.onAddTypeChange}
      onConfirmAdd={chrome.onConfirmAdd}
      onCancelAdd={chrome.onCancelAdd}
      typeOptionsForAdd={chrome.typeOptionsForAdd}
      rulesTreeData={chrome.rulesTreeData}
    />
  );
}

/** Stable arborist row type — editingId comes from context, not a new renderRow closure. */
function ArboristLocationRow(props: {
  node: { isSelected: boolean; data: LocationTreeNode };
  innerRef: (el: HTMLDivElement | null) => void;
  attrs: React.HTMLAttributes<HTMLDivElement>;
  children: React.ReactElement;
}) {
  const chrome = useLocationTreeChrome();
  return (
    <LocationTreeRow
      {...props}
      enabledLocationIds={chrome.enabledLocationIds}
      highlightedLocationIds={chrome.highlightedLocationIds}
      activeLocationId={chrome.activeLocationId}
      editingId={chrome.editingId}
    />
  );
}

export function LocationHierarchyTree({
  projectId,
  onSelectLocation,
  onSelectionChange,
  onQuickSelectLocation,
  quickSelectLabel = 'Set',
  onDataLoad,
  onRename,
  showRenameHint = true,
  selectedLocationId = null,
  highlightedLocationIds,
  activeLocationId = null,
  pendingChatMarks,
  width = 400,
  height = 360,
  disableDrag = true,
  disableEdit = false,
  searchTerm = '',
  searchMatch,
  enabledLocationIds = null,
  dataOverride = null,
  onMove,
  onRemoveNode,
  treeApiRef,
  variant = 'default',
  onAddChild,
  onConfirmAddChild,
  pendingAddParentId = null,
  onPendingAddParentConsumed,
  inlineAdd = null,
  onInlineAddChange,
  onDuplicateNode,
  onChangeType,
  locationTypeOptions,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: LocationHierarchyTreeProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const editingIdRef = useRef<string | null>(null);
  const editNameRef = useRef('');
  editingIdRef.current = editingId;
  editNameRef.current = editName;
  const [internalAddParentId, setInternalAddParentId] = useState<string | null>(null);
  const [internalAddName, setInternalAddName] = useState('');
  const [internalAddType, setInternalAddType] = useState('general');
  const inlineAddControlled = onInlineAddChange != null;
  const addParentId = inlineAddControlled ? (inlineAdd?.parentId ?? null) : internalAddParentId;
  const addName = inlineAddControlled ? (inlineAdd?.name ?? '') : internalAddName;
  const addType = inlineAddControlled ? (inlineAdd?.locationType ?? 'general') : internalAddType;

  const patchInlineAdd = useCallback(
    (patch: Partial<{ parentId: string; name: string; locationType: string }> & { parentId?: string | null }) => {
      if (patch.parentId === null) {
        if (inlineAddControlled) onInlineAddChange?.(null);
        else {
          setInternalAddParentId(null);
          setInternalAddName('');
          setInternalAddType('general');
        }
        return;
      }
      const parentId = patch.parentId ?? addParentId;
      if (!parentId) return;
      const next = {
        parentId,
        name: patch.name ?? addName,
        locationType: patch.locationType ?? addType,
      };
      if (inlineAddControlled) onInlineAddChange?.(next);
      else {
        setInternalAddParentId(next.parentId);
        setInternalAddName(next.name);
        setInternalAddType(next.locationType);
      }
    },
    [inlineAddControlled, onInlineAddChange, addParentId, addName, addType],
  );
  const internalTreeApiRef = useRef<TreeApi<LocationTreeNode> | null>(null);

  const setTreeApi = useCallback(
    (api: TreeApi<LocationTreeNode> | null) => {
      internalTreeApiRef.current = api;
      if (!treeApiRef) return;
      if (typeof treeApiRef === 'function') treeApiRef(api);
      else (treeApiRef as React.MutableRefObject<TreeApi<LocationTreeNode> | null>).current = api;
    },
    [treeApiRef],
  );

  const defaultSearchMatch = React.useCallback((node: { data: LocationTreeNode }, term: string) => {
    if (isSyntheticRow(node.data)) return true; // keep synthetic rows visible while filtering parent
    const t = term.trim().toLowerCase();
    if (!t) return true;
    const d = node.data;
    const name = (d.name ?? '').toLowerCase();
    const type = (d.location_type ?? '').toLowerCase();
    return name.includes(t) || type.includes(t);
  }, []);
  const matchFn = searchMatch ?? defaultSearchMatch;
  const storeData = useLocationsTreeStore((s) => (projectId ? s.treeByProjectId[projectId] ?? null : null));
  const loading = useLocationsTreeStore((s) => s.loadingProjectId === projectId);
  const error = useLocationsTreeStore((s) => (projectId ? s.errorByProjectId[projectId] ?? null : null));
  const fetchTreeIfNeeded = useLocationsTreeStore((s) => s.fetchTreeIfNeeded);
  const onDataLoadRef = useRef(onDataLoad);
  onDataLoadRef.current = onDataLoad;

  const useOverride = dataOverride != null && dataOverride.length >= 0;
  const rawData = useOverride ? dataOverride : storeData;

  const data = useMemo(() => {
    if (!rawData) return rawData;
    if (variant !== 'management') return rawData;
    return injectManagementRows(rawData, addParentId);
  }, [rawData, variant, addParentId]);

  const typeOptionsForAdd = useMemo(() => {
    const base = (locationTypeOptions?.length
      ? locationTypeOptions
      : collectLocationTypes(rawData ?? []).map((t) => ({
          value: t,
          label: formatLocationTypeLabel(t),
        }))
    ).filter((o) => normalizeLocationTypeKey(o.value) !== 'project');
    const normalized =
      base.length === 0
        ? [{ value: 'general', label: 'General' }]
        : base.map((o) => ({
            value: o.value,
            label: o.label || formatLocationTypeLabel(o.value),
          }));
    const addParent =
      addParentId && rawData ? findNodeById(rawData, addParentId) : null;
    if (!addParent || !rawData) return normalized;
    return annotateLocationTypeOptions(normalized, addParent, rawData);
  }, [locationTypeOptions, rawData, addParentId]);

  useEffect(() => {
    if (!addParentId) return;
    const currentKey = normalizeLocationTypeKey(addType || 'general');
    const allowed = typeOptionsForAdd.find((o) => !o.disabled);
    const currentOk = typeOptionsForAdd.some(
      (o) => normalizeLocationTypeKey(o.value) === currentKey && !o.disabled,
    );
    if (!currentOk && allowed) {
      patchInlineAdd({ locationType: allowed.value });
    }
  }, [addParentId, addType, typeOptionsForAdd, patchInlineAdd]);

  useEffect(() => {
    if (!projectId || useOverride) return;
    fetchTreeIfNeeded(projectId)
      .then((next) => {
        onDataLoadRef.current?.(next, countNodes(next));
      })
      .catch(() => {});
  }, [projectId, fetchTreeIfNeeded, useOverride]);

  const startInlineAdd = useCallback(
    (parent: LocationTreeNode) => {
      if (!canCreate) return;
      const id = parent.location_id != null ? String(parent.location_id) : '';
      if (!id) return;
      // Management: open inline composer. Legacy: immediate callback.
      if (variant === 'management' && onConfirmAddChild) {
        setEditingId(null);
        patchInlineAdd({ parentId: id, name: '', locationType: 'general' });
        requestAnimationFrame(() => {
          try {
            internalTreeApiRef.current?.open(id);
          } catch {
            /* ignore */
          }
        });
        return;
      }
      onAddChild?.(parent);
    },
    [canCreate, variant, onConfirmAddChild, onAddChild, patchInlineAdd],
  );

  useEffect(() => {
    if (!pendingAddParentId || !rawData) return;
    const parent = findNodeById(rawData, pendingAddParentId);
    if (parent) startInlineAdd(parent);
    onPendingAddParentConsumed?.();
  }, [pendingAddParentId, rawData, startInlineAdd, onPendingAddParentConsumed]);

  const cancelInlineAdd = useCallback(() => {
    patchInlineAdd({ parentId: null });
  }, [patchInlineAdd]);

  const confirmInlineAdd = useCallback(() => {
    if (!addParentId || !onConfirmAddChild || !rawData) return;
    const name = addName.trim();
    if (!name) return;
    const parent = findNodeById(rawData, addParentId);
    if (!parent) return;
    onConfirmAddChild({
      parent,
      name,
      locationType: addType || 'general',
    });
    cancelInlineAdd();
  }, [addParentId, addName, addType, onConfirmAddChild, rawData, cancelInlineAdd]);

  const startInlineEdit = useCallback(
    (node: LocationTreeNode) => {
      if (!canEdit || !onRename) return;
      const id = node.location_id != null ? String(node.location_id) : '';
      if (!id) return;
      patchInlineAdd({ parentId: null });
      setEditingId(id);
      setEditName(node.name ?? '');
      // Custom TextInput owns editing (disableEdit is on for management). Do not call
      // arborist edit()/reset() — reset() re-focuses the edited row and, with
      // selectionFollowsFocus, steals selection back when clicking another node.
      // Do not select here — that opens the details panel during rename.
    },
    [canEdit, onRename, patchInlineAdd],
  );

  const cancelInlineEdit = useCallback(() => {
    setEditingId(null);
    setEditName('');
  }, []);

  const commitInlineEdit = useCallback(() => {
    const id = editingIdRef.current;
    if (!id || !onRename) {
      cancelInlineEdit();
      return;
    }
    // Another edit may have started after blur was scheduled.
    if (editingIdRef.current !== id) return;
    const trimmed = editNameRef.current.trim();
    const existing = rawData ? findNodeById(rawData, id) : null;
    if (trimmed && trimmed !== (existing?.name ?? '')) {
      void onRename({ id, name: trimmed });
    }
    if (editingIdRef.current === id) {
      cancelInlineEdit();
    }
  }, [onRename, rawData, cancelInlineEdit]);

  const chromeValue = useMemo<LocationTreeChromeContextValue>(
    () => ({
      onRemoveNode,
      onQuickSelectLocation,
      onFocusLocation:
        variant === 'picker'
          ? (location) => {
              onSelectionChange?.([location]);
            }
          : undefined,
      quickSelectLabel,
      enabledLocationIds,
      showRenameHint,
      variant,
      onStartAddChild: startInlineAdd,
      onDuplicateNode,
      onChangeType,
      locationTypeOptions: locationTypeOptions ?? [],
      canCreate,
      canEdit,
      canDelete,
      editingId,
      editName,
      onStartEdit: startInlineEdit,
      onEditNameChange: setEditName,
      onCommitEdit: commitInlineEdit,
      onCancelEdit: cancelInlineEdit,
      addName,
      addType,
      onAddNameChange: (value: string) => patchInlineAdd({ name: value }),
      onAddTypeChange: (value: string) => patchInlineAdd({ locationType: value }),
      onConfirmAdd: confirmInlineAdd,
      onCancelAdd: cancelInlineAdd,
      typeOptionsForAdd,
      rulesTreeData: rawData ?? [],
      highlightedLocationIds,
      activeLocationId,
      pendingChatMarks,
    }),
    [
      onRemoveNode,
      onQuickSelectLocation,
      onSelectionChange,
      quickSelectLabel,
      enabledLocationIds,
      showRenameHint,
      variant,
      startInlineAdd,
      onDuplicateNode,
      onChangeType,
      locationTypeOptions,
      canCreate,
      canEdit,
      canDelete,
      editingId,
      editName,
      startInlineEdit,
      commitInlineEdit,
      cancelInlineEdit,
      addName,
      addType,
      confirmInlineAdd,
      cancelInlineAdd,
      patchInlineAdd,
      typeOptionsForAdd,
      rawData,
      highlightedLocationIds,
      activeLocationId,
      pendingChatMarks,
    ],
  );

  if (!projectId) {
    return (
      <Text size="sm" c="dimmed" p="md">
        No project selected.
      </Text>
    );
  }

  if (!useOverride && loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <Loader size="sm" />
      </div>
    );
  }

  if (!useOverride && error) {
    return (
      <Alert color="red" title="Error">
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Text size="sm" c="dimmed" p="md">
        {useOverride ? 'No locations in AI result (or all removed).' : 'No locations found for this project.'}
      </Text>
    );
  }

  const managementUsesCustomEdit = variant === 'management';

  const shouldDisableDrop = useCallback(
    (args: {
      parentNode: { isRoot: boolean; data?: LocationTreeNode };
      dragNodes: Array<{ data?: LocationTreeNode }>;
    }) => {
      if (disableDrag) return true;
      if (!onMove) return false;
      const dropTree = rawData ?? [];
      const parentData =
        args.parentNode.isRoot ||
        !args.parentNode.data ||
        isSyntheticRow(args.parentNode.data)
          ? null
          : args.parentNode.data;

      for (const drag of args.dragNodes) {
        const node = drag?.data;
        if (!node || isSyntheticRow(node)) return true;
        if (isProjectRootNode(node)) return true;
        const placement = canPlaceLocationType({
          childType: node.location_type,
          parent: parentData,
          tree: dropTree,
          excludeNodeId: node.location_id != null ? String(node.location_id) : null,
        });
        if (!placement.ok) return true;
      }
      return false;
    },
    [disableDrag, onMove, rawData],
  );

  return (
    <LocationTreeChromeContext.Provider value={chromeValue}>
      <Tree<LocationTreeNode>
        ref={setTreeApi}
        data={data}
        idAccessor={locationTreeIdAccessor}
        childrenAccessor={
          variant === 'management' ? locationTreeManagementChildrenAccessor : locationTreeChildrenAccessor
        }
        width={width}
        height={height}
        rowHeight={variant === 'management' ? 45 : variant === 'picker' ? 40 : 36}
        indent={24}
        openByDefault={true}
        selectionFollowsFocus
        selection={highlightedLocationIds ? undefined : (selectedLocationId ?? undefined)}
        disableDrag={disableDrag}
        disableDrop={shouldDisableDrop}
        disableEdit={managementUsesCustomEdit || disableEdit || !onRename}
        onRename={managementUsesCustomEdit ? undefined : onRename}
        onMove={onMove}
        searchTerm={searchTerm}
        searchMatch={(node, term) => matchFn(node as { data: LocationTreeNode }, term)}
        renderRow={ArboristLocationRow}
        onSelect={(nodes) => {
          const filtered = nodes.filter((n) => !isSyntheticRow(n.data));
          const selectedIds = filtered
            .map((n) => (n.data?.location_id != null ? String(n.data.location_id) : ''))
            .filter(Boolean);
          // Commit / leave inline edit when selection moves to a different node.
          const editing = editingIdRef.current;
          if (editing && selectedIds.length > 0 && !selectedIds.includes(editing)) {
            commitInlineEdit();
          }
          // Hide empty inline-add composer when selecting a node other than its parent.
          if (addParentId && !addName.trim()) {
            const keepsAddParent =
              selectedIds.length > 0 && selectedIds.every((id) => id === addParentId);
            if (selectedIds.length > 0 && !keepsAddParent) {
              cancelInlineAdd();
            }
          }
          // Picker: selection/focus follows any click. Scope is enforced only by the Set button.
          if (variant === 'picker' || enabledLocationIds == null) {
            onSelectionChange?.(filtered.map((n) => n.data));
            return;
          }
          const allowed = filtered.filter(
            (n) => n.data?.location_id != null && enabledLocationIds.has(String(n.data.location_id)),
          );
          onSelectionChange?.(allowed.map((n) => n.data));
        }}
      >
        {ArboristLocationNode}
      </Tree>
    </LocationTreeChromeContext.Provider>
  );
}
